import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ContentItem } from '../../../models/ContentManagementInterface';
import type { ImageCardsCropMeta } from './useImageCardsSplit';

export type SidebarTab = 'blocks' | 'media' | 'documents';

/** Where a video clicked in the media tab goes — set when the tab is opened from a specific slide. */
export interface MediaPickTarget {
  label: string;
  onPick: (externalId: string) => void;
}

/**
 * Target for the documents tab — picking a document opens the same viewer as
 * library import; onImported receives the AI blocks for the originating section.
 */
export interface DocumentPickTarget {
  /** Label shown in the viewer: which block/editor opened it. */
  label: string;
  /**
   * Same AI blocks a library import would append, applied to this section.
   * `cropMeta` is set only when the import detected image cards from a
   * single page — lets the caller offer the same "Adjust crops" pass a
   * screenshot paste would, instead of just dropping the crops in as-is.
   */
  onImported: (sections: ContentItem[], cropMeta?: ImageCardsCropMeta | null) => void;
}

interface EditorSidebarValue {
  open: boolean;
  tab: SidebarTab;
  pickTarget: MediaPickTarget | null;
  documentPickTarget: DocumentPickTarget | null;
  /** The library video currently being dragged out of the media tab, or null. */
  draggingVideoId: string | null;
  openSidebar: (tab: 'blocks' | 'media', pickTarget?: MediaPickTarget) => void;
  openSidebarDocuments: (pickTarget?: DocumentPickTarget) => void;
  closeSidebar: () => void;
  selectTab: (tab: SidebarTab) => void;
  startVideoDrag: (externalId: string) => void;
  endVideoDrag: () => void;
  /** Appends AI-imported blocks from the Documents library viewer. */
  importSections: (sections: ContentItem[], cropMeta?: ImageCardsCropMeta | null) => void;
}

const EditorSidebarContext = createContext<EditorSidebarValue>({
  open: false,
  tab: 'blocks',
  pickTarget: null,
  documentPickTarget: null,
  draggingVideoId: null,
  openSidebar: () => {},
  openSidebarDocuments: () => {},
  closeSidebar: () => {},
  selectTab: () => {},
  startVideoDrag: () => {},
  endVideoDrag: () => {},
  importSections: () => {},
});

/**
 * Open/closed state and active tab of the v2 editor's left sidebar (Blocks,
 * Media, and Documents), plus the in-flight video drag. Lifted out of the
 * sidebar itself so a slide deep in the tree can open the media tab aimed at
 * itself, and so slides can reveal themselves as drop targets while a video is
 * being dragged.
 */
function useEditorSidebarValue(
  onImportSections?: (sections: ContentItem[], cropMeta?: ImageCardsCropMeta | null) => void,
): EditorSidebarValue {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<SidebarTab>('blocks');
  const [pickTarget, setPickTarget] = useState<MediaPickTarget | null>(null);
  const [documentPickTarget, setDocumentPickTarget] = useState<DocumentPickTarget | null>(null);
  const [draggingVideoId, setDraggingVideoId] = useState<string | null>(null);

  const openSidebar = useCallback((nextTab: 'blocks' | 'media', target?: MediaPickTarget) => {
    setTab(nextTab);
    setPickTarget(target ?? null);
    setDocumentPickTarget(null);
    setOpen(true);
  }, []);

  const openSidebarDocuments = useCallback((target?: DocumentPickTarget) => {
    setTab('documents');
    setDocumentPickTarget(target ?? null);
    setPickTarget(null);
    setOpen(true);
  }, []);

  const closeSidebar = useCallback(() => {
    setOpen(false);
    setPickTarget(null);
    setDocumentPickTarget(null);
  }, []);

  const selectTab = useCallback((nextTab: SidebarTab) => {
    setTab(nextTab);
    setPickTarget(null);
    setDocumentPickTarget(null);
  }, []);

  const startVideoDrag = useCallback((externalId: string) => setDraggingVideoId(externalId), []);
  const endVideoDrag = useCallback(() => setDraggingVideoId(null), []);
  const importSections = useCallback(
    (sections: ContentItem[], cropMeta?: ImageCardsCropMeta | null) =>
      onImportSections?.(sections, cropMeta),
    [onImportSections],
  );

  return useMemo<EditorSidebarValue>(
    () => ({
      open,
      tab,
      pickTarget,
      documentPickTarget,
      draggingVideoId,
      openSidebar,
      openSidebarDocuments,
      closeSidebar,
      selectTab,
      startVideoDrag,
      endVideoDrag,
      importSections,
    }),
    [
      open,
      tab,
      pickTarget,
      documentPickTarget,
      draggingVideoId,
      openSidebar,
      openSidebarDocuments,
      closeSidebar,
      selectTab,
      startVideoDrag,
      endVideoDrag,
      importSections,
    ],
  );
}

export function EditorSidebarProvider({
  children,
  onImportSections,
}: {
  children: React.ReactNode;
  onImportSections?: (sections: ContentItem[], cropMeta?: ImageCardsCropMeta | null) => void;
}) {
  const value = useEditorSidebarValue(onImportSections);
  return <EditorSidebarContext.Provider value={value}>{children}</EditorSidebarContext.Provider>;
}

export function useEditorSidebar(): EditorSidebarValue {
  return useContext(EditorSidebarContext);
}
